/**
 * "Actions" menu for the matchUps tab.
 *
 * Currently surfaces `abandonTournamentMatchUps`, which bulk-sets every
 * readyToScore matchUp to ABANDONED. Intended for end-of-tournament cleanup:
 * when a tournament will not be completed, its remaining matchUps should not be
 * left in a readyToScore state. The action is only offered once the tournament
 * is on or past its last date (endDate).
 */
import { ABANDON_TOURNAMENT_MATCHUPS } from 'constants/mutationConstants';
import { getRepublishRankingsOption } from 'pages/tournament/tabs/matchUpsTab/republishRankingsAction';
import { confirmModal } from 'components/modals/baseModal/baseModal';
import { mutationRequest } from 'services/mutation/mutationRequest';
import { venueToday } from 'functions/venueTimeFrame';
import { tmxToast } from 'services/notifications/tmxToast';
import { tournamentEngine } from 'services/factory/engine';
import { RIGHT } from 'constants/tmxConstants';
import { t } from 'i18n';

// Calendar-day comparison via ISO `YYYY-MM-DD` string compare. Avoids the
// `new Date("YYYY-MM-DD")` UTC-midnight off-by-one that renders the wrong day
// west of UTC. True when today is on or after the tournament's last date.
//
// "Today" is the VENUE's day, not the device's. `endDate` is a venue calendar
// day, so comparing it against the operator's would offer — or withhold — the
// abandon action a day early or late for anyone running the event from another
// zone. The previous `dayjs().format('YYYY-MM-DD')` fixed the UTC half of this
// (#1352's bug class) and left the operator-vs-venue half, which is what
// `venueToday()` closes.
function onOrPastLastDate(): boolean {
  const { tournamentRecord } = tournamentEngine.getTournament() ?? {};
  const endDate = tournamentRecord?.endDate;
  if (!endDate) return false;
  return venueToday() >= String(endDate).slice(0, 10);
}

// Modal body: a one-line explanation plus a checkbox that relaxes the default
// `requireNoScore` guard so matchUps with partial scores can also be abandoned.
function buildAbandonContent(): { element: HTMLElement; getRequireNoScore: () => boolean } {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'display:flex;flex-direction:column;gap:12px;font-size:0.875rem;';

  const summary = document.createElement('div');
  summary.textContent = t('matchUpsActions.abandonExplain');
  wrap.appendChild(summary);

  const label = document.createElement('label');
  label.style.cssText = 'display:flex;align-items:center;gap:8px;cursor:pointer;';
  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  const text = document.createElement('span');
  text.textContent = t('matchUpsActions.abandonPartial');
  label.appendChild(checkbox);
  label.appendChild(text);
  wrap.appendChild(label);

  return { element: wrap, getRequireNoScore: () => !checkbox.checked };
}

function runAbandon(requireNoScore: boolean, onDone?: () => void): void {
  mutationRequest({
    methods: [{ method: ABANDON_TOURNAMENT_MATCHUPS, params: { requireNoScore } }],
    callback: (result: any) => {
      if (result?.error) return;
      const count = result?.results?.[0]?.abandoned ?? 0;
      tmxToast({
        message: count ? `Abandoned ${count} match${count === 1 ? '' : 'es'}` : 'No matches to abandon',
        intent: count ? 'is-success' : 'is-info',
      });
      onDone?.();
    },
  });
}

function showAbandonModal(onDone?: () => void): void {
  const { element, getRequireNoScore } = buildAbandonContent();
  confirmModal({
    title: t('matchUpsActions.abandonRemaining'),
    query: element,
    okIntent: 'is-danger',
    okAction: () => runAbandon(getRequireNoScore(), onDone),
  });
}

// Returns the controlBar dropdown item for `options_right`, or null when the
// tournament is not yet on/past its last date (caller filters nulls out).
export function getActionsItem(replaceTableData: () => void): any | null {
  const options: any[] = [];

  // Abandon remaining matches — only on/past the tournament's last date.
  if (onOrPastLastDate()) {
    options.push({
      onClick: () => showAbandonModal(replaceTableData),
      label: t('matchUpsActions.abandonRemaining'),
      intent: 'is-danger',
      close: true,
    });
  }

  // Republish rankings — admin-only (null when not an admin of the active provider).
  const republish = getRepublishRankingsOption();
  if (republish) options.push(republish);

  if (!options.length) return null;
  return { options, id: 'matchUpsActions', location: RIGHT, label: t('actions.actions'), align: RIGHT };
}
